import { useCallback } from 'react'
import { format } from 'date-fns' // eslint-disable-line
import { ptBR } from 'date-fns/locale' // eslint-disable-line
import jsPDF from 'jspdf'
import axiosInstance from 'src/utils/axios'
import { IOrderTableFilters } from 'src/types/orders'

interface OrdersData {
  data: any[]
  extraData: any
  total: number
}

const PAYMENT_TYPE_LABELS: Record<string, string> = {
  money: 'Dinheiro',
  credit: 'Cartão de Crédito',
  debit: 'Cartão de Débito',
  pix: 'Pix',
  card: 'CashLess',
}

const fmtCurrency = (v: number) => {
  if (!v) return 'R$ 0,00'
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v / 100)
}

function loadLogoAsDataUrl(): Promise<string | null> {
  return new Promise((resolve) => {
    // eslint-disable-next-line
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.naturalWidth
      canvas.height = img.naturalHeight
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        resolve(null)
        return
      }
      ctx.drawImage(img, 0, 0)
      resolve(canvas.toDataURL('image/png'))
    }
    img.onerror = () => resolve(null)
    img.src = '/logo/logo.svg'
  })
}

export default function useOrdersExport(data: OrdersData | undefined, filters: IOrderTableFilters) {
  const handleExportExcel = useCallback(() => {
    if (!data?.data || data.data.length === 0) {
      alert('Não há dados para exportar')
      return
    }

    const exportData = data.data.map((item: any) => ({
      Pedido: item.number || '',
      Evento: item.event?.name || '',
      Vendedor: item.saler?.name || '',
      'Meio de Pagamento': PAYMENT_TYPE_LABELS[item.paymentType] || item.paymentType,
      'Terminal Serial': item.terminalSerial || '',
      Total: `R$ ${(item.total / 100 || 0).toFixed(2).replace('.', ',')}`,
      Taxa: `R$ ${(item.tax / 100 || 0).toFixed(2).replace('.', ',')}`,
      Líquido: `R$ ${((item.total - item.tax) / 100 || 0).toFixed(2).replace('.', ',')}`,
      Data: format(new Date(item.createdAt), 'dd/MM/yyyy HH:mm:ss'),
    }))

    const headers = Object.keys(exportData[0])
    const csvContent = [
      headers.join(';'),
      ...exportData.map((row) =>
        headers.map((header) => `"${row[header as keyof typeof row]}"`).join(';'),
      ),
    ].join('\n')

    const blob = new Blob([`\ufeff${csvContent}`], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `pedidos-${new Date().toISOString().split('T')[0]}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }, [data])

  const handleExportPDF = useCallback(async () => {
    if (!data?.extraData || !filters.event) return

    const ed = data.extraData

    let eventTaxConfig: Record<string, number> | null = null
    try {
      const res = await axiosInstance.get(`/event/${filters.event.value}`)
      eventTaxConfig = res.data?.event?.tax || null
    } catch {
      // ignore
    }

    const logoDataUrl = await loadLogoAsDataUrl()

    // eslint-disable-next-line new-cap
    const doc = new jsPDF()
    const pageWidth = doc.internal.pageSize.getWidth()
    const margin = 20
    const contentWidth = pageWidth - margin * 2
    let y = 20

    // --- Header ---
    const headerHeight = 45
    doc.setFillColor(102, 126, 234)
    doc.rect(0, 0, pageWidth, headerHeight, 'F')

    doc.setTextColor(255, 255, 255)
    doc.setFontSize(22)
    doc.setFont('helvetica', 'bold')
    doc.text('Fechamento de Evento', margin, 22)
    doc.setFontSize(12)
    doc.setFont('helvetica', 'normal')
    doc.text(filters.event.label || 'Evento', margin, 33)
    doc.setFontSize(9)
    doc.text(
      `Gerado em ${format(new Date(), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR })}`,
      margin,
      41,
    )

    y = headerHeight + 13

    // --- Taxas Configuradas ---
    if (eventTaxConfig) {
      doc.setTextColor(50, 50, 50)
      doc.setFontSize(14)
      doc.setFont('helvetica', 'bold')
      doc.text('Taxas Configuradas no Evento', margin, y)
      y += 3
      doc.setDrawColor(102, 126, 234)
      doc.setLineWidth(0.8)
      doc.line(margin, y, margin + 60, y)
      y += 10

      const taxTypes = ['credit', 'debit', 'money', 'pix'] as const
      taxTypes.forEach((type, idx) => {
        const rowY = y + idx * 10
        if (idx % 2 === 0) {
          doc.setFillColor(245, 247, 250)
          doc.rect(margin, rowY - 5, contentWidth, 10, 'F')
        }
        doc.setFont('helvetica', 'normal')
        doc.setFontSize(10)
        doc.setTextColor(100, 100, 100)
        doc.text(PAYMENT_TYPE_LABELS[type], margin + 4, rowY)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(30, 30, 30)
        const rate = eventTaxConfig?.[type] ?? 0
        doc.text(`${rate}%`, pageWidth - margin - 4, rowY, { align: 'right' })
      })

      y += taxTypes.length * 10 + 8
    }

    // --- Resumo Geral ---
    doc.setTextColor(50, 50, 50)
    doc.setFontSize(14)
    doc.setFont('helvetica', 'bold')
    doc.text('Resumo Geral', margin, y)
    y += 3

    doc.setDrawColor(102, 126, 234)
    doc.setLineWidth(0.8)
    doc.line(margin, y, margin + 40, y)
    y += 10

    const summaryItems = [
      { label: 'Total de Vendas (qtd)', value: String(data.total || 0) },
      { label: 'Faturamento Bruto', value: fmtCurrency(ed.totalValue || 0) },
      { label: 'Total de Taxas', value: fmtCurrency(ed.totalTax || 0) },
      { label: 'Valor Líquido', value: fmtCurrency(ed.organizedTax || 0) },
      {
        label: 'Ticket Médio',
        value:
          (data.total || 0) > 0
            ? fmtCurrency((ed.totalValue || 0) / (data.total || 1))
            : 'R$ 0,00',
      },
    ]

    summaryItems.forEach((item, idx) => {
      const rowY = y + idx * 10
      if (idx % 2 === 0) {
        doc.setFillColor(245, 247, 250)
        doc.rect(margin, rowY - 5, contentWidth, 10, 'F')
      }
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(10)
      doc.setTextColor(100, 100, 100)
      doc.text(item.label, margin + 4, rowY)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(30, 30, 30)
      doc.text(item.value, pageWidth - margin - 4, rowY, { align: 'right' })
    })

    y += summaryItems.length * 10 + 12

    // --- Vendas por Tipo de Pagamento ---
    doc.setTextColor(50, 50, 50)
    doc.setFontSize(14)
    doc.setFont('helvetica', 'bold')
    doc.text('Vendas por Tipo de Pagamento', margin, y)
    y += 3
    doc.setDrawColor(102, 126, 234)
    doc.setLineWidth(0.8)
    doc.line(margin, y, margin + 65, y)
    y += 10

    doc.setFillColor(102, 126, 234)
    doc.rect(margin, y - 5, contentWidth, 10, 'F')
    doc.setTextColor(255, 255, 255)
    doc.setFontSize(9)
    doc.setFont('helvetica', 'bold')
    const colX = {
      tipo: margin + 4,
      config: margin + contentWidth * 0.28,
      bruto: margin + contentWidth * 0.42,
      taxa: margin + contentWidth * 0.58,
      liquido: margin + contentWidth * 0.76,
      percent: pageWidth - margin - 4,
    }
    doc.text('Tipo', colX.tipo, y)
    if (eventTaxConfig) doc.text('Taxa Conf.', colX.config, y)
    doc.text('Bruto', colX.bruto, y)
    doc.text('Taxa Cobrada', colX.taxa, y)
    doc.text('Líquido', colX.liquido, y)
    doc.text('%', colX.percent, y, { align: 'right' })
    y += 10

    const paymentEntries = Object.entries(ed.paymentTypeValues || {}).filter(
      ([type]) => type !== 'card',
    )

    paymentEntries.forEach(([type, amount]: [string, any], idx) => {
      const gross = Number(amount) || 0
      const tax = Number(ed.tax?.[type]) || 0
      const net = gross - tax
      const pct = (ed.totalValue || 0) > 0 ? ((gross / ed.totalValue) * 100).toFixed(1) : '0.0'

      if (idx % 2 === 0) {
        doc.setFillColor(245, 247, 250)
        doc.rect(margin, y - 5, contentWidth, 10, 'F')
      }

      doc.setFont('helvetica', 'bold')
      doc.setTextColor(30, 30, 30)
      doc.setFontSize(9)
      doc.text(PAYMENT_TYPE_LABELS[type] || type, colX.tipo, y)
      doc.setFont('helvetica', 'normal')
      if (eventTaxConfig) {
        doc.setTextColor(80, 80, 80)
        doc.text(`${eventTaxConfig[type] ?? 0}%`, colX.config, y)
      }
      doc.setTextColor(30, 30, 30)
      doc.text(fmtCurrency(gross), colX.bruto, y)
      doc.setTextColor(200, 50, 50)
      doc.text(`- ${fmtCurrency(tax)}`, colX.taxa, y)
      doc.setTextColor(34, 139, 34)
      doc.setFont('helvetica', 'bold')
      doc.text(fmtCurrency(net), colX.liquido, y)
      doc.setTextColor(100, 100, 100)
      doc.setFont('helvetica', 'normal')
      doc.text(`${pct}%`, colX.percent, y, { align: 'right' })

      y += 10
    })

    // Total row
    doc.setDrawColor(50, 50, 50)
    doc.setLineWidth(0.5)
    doc.line(margin, y - 5, margin + contentWidth, y - 5)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(10)
    doc.setTextColor(30, 30, 30)
    doc.text('TOTAL', colX.tipo, y)
    doc.text(fmtCurrency(ed.totalValue || 0), colX.bruto, y)
    doc.setTextColor(200, 50, 50)
    doc.text(`- ${fmtCurrency(ed.totalTax || 0)}`, colX.taxa, y)
    doc.setTextColor(34, 139, 34)
    doc.text(fmtCurrency(ed.organizedTax || 0), colX.liquido, y)
    doc.setTextColor(100, 100, 100)
    doc.text('100%', colX.percent, y, { align: 'right' })

    y += 20

    // --- Filtros aplicados ---
    if (filters.dateStart || filters.dateEnd) {
      doc.setTextColor(120, 120, 120)
      doc.setFontSize(8)
      doc.setFont('helvetica', 'italic')
      const dateRange = [
        filters.dateStart ? `De: ${format(filters.dateStart, 'dd/MM/yyyy HH:mm')}` : '',
        filters.dateEnd ? `Até: ${format(filters.dateEnd, 'dd/MM/yyyy HH:mm')}` : '',
      ]
        .filter(Boolean)
        .join('  |  ')
      doc.text(`Período filtrado: ${dateRange}`, margin, y)
      y += 8
    }

    // --- Footer ---
    const footerY = 280
    doc.setDrawColor(200, 200, 200)
    doc.setLineWidth(0.3)
    doc.line(margin, footerY, pageWidth - margin, footerY)

    if (logoDataUrl) {
      const logoH = 5
      const logoW = logoH * (3652 / 735)
      doc.addImage(logoDataUrl, 'PNG', margin, footerY + 1.5, logoW, logoH)
    }

    doc.setTextColor(160, 160, 160)
    doc.setFontSize(7)
    doc.setFont('helvetica', 'normal')
    const textX = logoDataUrl ? margin + 30 : margin
    doc.text('DevBrasil Events - Relatório de Fechamento', textX, footerY + 6)
    doc.text('Documento gerado automaticamente', pageWidth - margin, footerY + 6, {
      align: 'right',
    })

    const eventSlug = (filters.event.label || 'evento').replace(/\s+/g, '-').toLowerCase()
    doc.save(`fechamento-${eventSlug}-${format(new Date(), 'yyyy-MM-dd')}.pdf`)
  }, [data, filters.event, filters.dateStart, filters.dateEnd])

  return { handleExportExcel, handleExportPDF }
}
